import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { 
  Box, Typography, CircularProgress, Accordion, AccordionSummary, AccordionDetails 
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, PlayCircle as PlayIcon } from '@mui/icons-material';

import courseService from '../services/courseService';
import enrollmentService from '../services/enrollmentService';
import videoService from '../services/videoService';
import playlistService from '../services/playlistService';
import commentService from '../services/commentService';
import ProtectedVideoPlayer from '../components/ProtectedVideoPlayer';
import CommentSection from '../components/CommentSection';

const LearnPage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Selected Content State (Only Videos now)
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Video streaming states
  const [videoStreamData, setVideoStreamData] = useState(null);
  const [isFetchingVideo, setIsFetchingVideo] = useState(false);

  // Comment states
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState('');

  // --- Helpers ---

  const fetchCommentsForVideo = async (videoId) => {
    setLoadingComments(true);
    setCommentError('');
    setComments([]);
    try {
      const response = await commentService.getCommentsByVideo(videoId);
      if (response.success) {
        setComments(response.data || []);
      } else {
        setCommentError(response.message || 'Failed to load comments.');
      }
    } catch (err) {
      setCommentError('Failed to load comments. Please try again.');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleVideoSelect = useCallback(async (video) => {
    if (selectedVideo?.id === video.id) return; // Don't reload if same video

    setSelectedVideo(video);
    setCommentError('');
    setIsFetchingVideo(true);
    setVideoStreamData(null);
    setComments([]);

    try {
      // 1. Fetch Stream URL
      const streamResponse = await videoService.getVideoStreamUrl(video.id);
      if (streamResponse.success) {
        // streamResponse.data now contains streamUrl AND signatureParams from our backend updates
        setVideoStreamData(streamResponse.data);
      } else {
        setError(streamResponse.message || 'Failed to load video');
      }

      // 2. Fetch Comments
      fetchCommentsForVideo(video.id);

    } catch (err) {
      setError('Failed to load video content.');
      setCommentError('Could not load comments.');
    } finally {
      setIsFetchingVideo(false);
    }
  }, [selectedVideo]);

  const handleAddComment = async (videoId, text) => {
    setCommentError('');
    try {
      const response = await commentService.createComment(videoId, text);
      if (response.success && response.data) {
        setComments(prevComments => [response.data, ...prevComments]);
        return true;
      } else {
        setCommentError(response.message || 'Failed to post comment.');
        return false;
      }
    } catch (err) {
      setCommentError('An error occurred while posting your comment.');
      return false;
    }
  };

  // Helper to calculate total videos
  const getTotalVideos = () => {
    return playlists.reduce((acc, playlist) => acc + (playlist.videos?.length || 0), 0);
  };

  // --- Effects ---

  useEffect(() => {
    const checkAccessAndFetchContent = async () => {
      try {
        setLoading(true);
        setError('');
        
        const enrolled = await enrollmentService.checkEnrollment(courseId);
        setIsEnrolled(enrolled);

        if (enrolled) {
          // Fetch Course & Playlists in parallel
          const [courseData, playlistsData] = await Promise.all([
            courseService.getCourseById(courseId),
            playlistService.getPlaylistsByCourseId(courseId)
          ]);

          setCourse(courseData);
          setPlaylists(playlistsData || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load course content');
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      checkAccessAndFetchContent();
    }
  }, [courseId]);

  // Auto-play first video on load
  useEffect(() => {
    if (playlists.length > 0 && !selectedVideo) {
      const firstPlaylistWithVideos = playlists.find(p => p.videos && p.videos.length > 0);
      if (firstPlaylistWithVideos) {
        const firstVideo = firstPlaylistWithVideos.videos[0];
        handleVideoSelect(firstVideo);
      }
    }
  }, [playlists, selectedVideo, handleVideoSelect]);


  // --- Render ---

  if (loading) {
     return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <CircularProgress />
        </div>
      );
  }

  if (!isEnrolled) {
    return <Navigate to={`/courses/${courseId}`} replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-3">
            {course?.title}
          </h1>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
              Enrolled
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
              {getTotalVideos()} Videos
            </span>
          </div>
        </div>

        {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
            </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* LEFT COLUMN: Video Player & Comments */}
          <div className="lg:col-span-2 space-y-4">
             {/* Player Container */}
             <div className="bg-black rounded-xl overflow-hidden aspect-video shadow-md relative">
              {isFetchingVideo ? (
                <div className="flex items-center justify-center h-full">
                    <CircularProgress sx={{ color: 'white' }} />
                </div>
              ) : selectedVideo && videoStreamData ? (
                // CHANGED: Pass streamUrl and signatureParams directly to the new HLS player
                <ProtectedVideoPlayer 
                  streamUrl={videoStreamData.streamUrl} 
                  signatureParams={videoStreamData.signatureParams}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-white">
                  <p>Select a video to start learning</p>
                </div>
              )}
            </div>

            {/* Video Title */}
            {selectedVideo && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-xl font-heading font-semibold text-slate-900">
                  {selectedVideo.title}
                </h2>
              </div>
            )}

            {/* Comments Section */}
            {selectedVideo && (
                <CommentSection
                    videoId={selectedVideo.id}
                    comments={comments}
                    onAddComment={handleAddComment}
                    loading={loadingComments}
                    error={commentError}
                />
            )}
          </div>

          {/* RIGHT COLUMN: Playlist Sidebar */}
          <div className="lg:col-span-1">
             <div className="bg-white rounded-xl border border-slate-200 overflow-hidden sticky top-6 shadow-sm">
                <div className="bg-slate-900 px-4 py-3">
                  <h3 className="text-sm font-semibold text-white">Course Content</h3>
                </div>
                
                <div className="max-h-[calc(100vh-8rem)] overflow-y-auto bg-slate-50">
                  {playlists.length > 0 ? (
                    playlists.map((playlist) => (
                      <Accordion 
                        key={playlist.id} 
                        defaultExpanded={true}
                        disableGutters 
                        elevation={0}
                        sx={{ 
                          '&:before': { display: 'none' },
                          borderBottom: '1px solid #e2e8f0'
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ 
                            bgcolor: '#fff', 
                            flexDirection: 'row-reverse',
                            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                              transform: 'rotate(90deg)',
                            },
                            '& .MuiAccordionSummary-content': {
                              marginLeft: 1,
                            }
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="600" color="text.primary">
                            {playlist.name}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                           {playlist.videos && playlist.videos.length > 0 ? (
                             playlist.videos.map((video, idx) => {
                               const isSelected = selectedVideo?.id === video.id;
                               return (
                                 <button
                                   key={video.id}
                                   onClick={() => handleVideoSelect(video)}
                                   className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors border-b border-slate-100 last:border-0 ${
                                     isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-slate-100 bg-slate-50'
                                   }`}
                                 >
                                   <div className={`mt-0.5 ${isSelected ? 'text-blue-600' : 'text-slate-400'}`}>
                                      <PlayIcon fontSize="small" />
                                   </div>
                                   <div>
                                      <Typography 
                                        variant="body2" 
                                        color={isSelected ? 'text.primary' : 'text.secondary'}
                                        fontWeight={isSelected ? 600 : 400}
                                      >
                                        {video.title}
                                      </Typography>
                                      {video.duration && (
                                        <Typography variant="caption" color="text.disabled">
                                          {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')} mins
                                        </Typography>
                                      )}
                                   </div>
                                 </button>
                               );
                             })
                           ) : (
                             <Box p={2} textAlign="center">
                               <Typography variant="caption" color="text.disabled">No videos in this playlist</Typography>
                             </Box>
                           )}
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    <Box p={4} textAlign="center">
                       <Typography variant="body2" color="text.secondary">No content available</Typography>
                    </Box>
                  )}
                </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LearnPage;